import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaSpinner } from "react-icons/fa6";
import axios from "axios";
import { TYPE_OF_FACILITY_OPTIONS, EMERGENCY_OPERATIONS_PLAN, US_STATE_ABBREVIATIONS } from "../common/Constants";
import { Snackbar, Alert } from '@mui/material';

export default function ContactUs() {
  // Use ref to capture actual email value from DOM, bypassing browser autofill issues
  const emailInputRef = useRef(null);

  const [formData, setFormData] = useState({
    applicant_name: "",
    center_school_name: "",
    position_title: "",
    phone: "",
    email: "",
    street_address: "",
    city: "",
    state: "",
    zipcode: "",
    type_of_facilities: [],
    number_of_staff: "",
    has_emergency_training: false,
    training_type: "",
    have_emergency_plan: false,
    emergency_plans: [],
    message: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isOpenNotification, setIsOpenNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync React state with DOM value after browser autofill
  // Browser autofill can bypass React's onChange, so we sync on blur
  const handleEmailBlur = () => {
    if (emailInputRef.current) {
      const domValue = emailInputRef.current.value?.trim() || '';
      // Sync state with DOM value if they differ (autofill scenario)
      if (domValue !== formData.email) {
        setFormData(prev => ({ ...prev, email: domValue }));
      }
    }
  };

  const handleChangeFormData = (type, e) => {
    const { value, checked } = e.target;

    if (type === "type_of_facilities" || type === "emergency_plans") {
      setFormData((prev) => {
        const newTypes = checked
          ? [...prev[type], value]
          : prev[type].filter((item) => item !== value);

        return {
          ...prev,
          [type]: newTypes,
        };
      });
    } else if (type === "email") {
      setFormData(prev => ({ ...prev, email: e.target.value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [type]: e.target.value === "true" ? true : e.target.value === "false" ? false : e.target.value
      }));
    }
  }

  const isValid = () => {
    if (
      !formData.applicant_name.trim() ||
      !formData.center_school_name.trim() ||
      !formData.position_title.trim() ||
      !formData.phone.trim() ||
      !formData.email.trim() ||
      !formData.street_address.trim() ||
      !formData.city.trim() ||
      !formData.state ||
      !formData.zipcode.trim() ||
      formData.type_of_facilities.length === 0 ||
      Number(formData.number_of_staff) < 1 ||
      (formData.has_emergency_training === true && !formData.training_type.trim()) ||
      (formData.have_emergency_plan === true && formData.emergency_plans.length === 0) ||
      !formData.message.trim()
    ) {
      setIsOpenNotification(true);
      setErrorMessage("All fields are required");
      return false;
    }

    // Validate email format and content
    const emailToValidate = formData.email.trim();

    if (!emailToValidate) {
      setIsOpenNotification(true);
      setErrorMessage("Please enter your email address.");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToValidate)) {
      setIsOpenNotification(true);
      setErrorMessage("Enter a valid email address");
      return false;
    }

    return true;
  };

  // Removed handleSendEmail function - backend's createApplication endpoint now handles all email sending

  const handleSubmit = async () => {
    console.log("formData on submit:", formData);
    if (!isValid()) {
      return;
    }

    if (isLoading) return;

    setIsLoading(true);

    try {
      console.log("Starting email send process...");

      // CRITICAL: Always read email from DOM first - it's the source of truth
      // formData.email can be stale if browser autofill bypassed React's onChange
      if (!emailInputRef.current) {
        setIsOpenNotification(true);
        setErrorMessage("Form error: Please refresh the page and try again.");
        setIsLoading(false);
        return;
      }

      // Read the ACTUAL current value from the DOM input element
      let userEmail = emailInputRef.current.value?.trim() || '';

      // If DOM is empty, fall back to formData (shouldn't happen if validation passed)
      if (!userEmail) {
        userEmail = formData.email.trim();
      }

      // Sync formData with DOM value to keep them in sync
      if (userEmail !== formData.email.trim()) {
        setFormData(prev => ({ ...prev, email: userEmail }));
      }

      console.log("📧 Final email value:", userEmail);

      // Final validation
      if (!userEmail) {
        setIsOpenNotification(true);
        setErrorMessage("Please enter your email address.");
        setIsLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userEmail)) {
        setIsOpenNotification(true);
        setErrorMessage("Please enter a valid email address.");
        setIsLoading(false);
        return;
      }

      console.log("📧 Email validation passed - User email:", userEmail);

      // Note: Backend's createApplication endpoint will send the full email template
      // No need to send a separate email from the frontend

      // Create application payload with user's actual email
      // CRITICAL: Read ALL values directly from DOM/form state at this moment to avoid stale closures
      const finalEmail = userEmail.trim();
      console.log("🔍 Creating application payload:");
      console.log("  Final email (from DOM):", finalEmail);
      console.log("  formData.email (for comparison):", formData.email);
      console.log("  Applicant name:", formData.applicant_name);

      // CRITICAL: Final validation
      if (!finalEmail) {
        console.error("❌ CRITICAL ERROR: finalEmail is empty");
        setIsOpenNotification(true);
        setErrorMessage("Email validation error. Please refresh the page and try again.");
        setIsLoading(false);
        return;
      }

      // Create payload object with email explicitly set to finalEmail (never use formData.email)
      // This ensures we're using a local variable, not a potentially stale formData.email
      const applicationPayload = {
        applicant_name: formData.applicant_name,
        center_school_name: formData.center_school_name,
        position_title: formData.position_title,
        phone: formData.phone,
        email: finalEmail, // CRITICAL: Use local variable finalEmail, NEVER formData.email
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipcode,
        type_of_facilities: formData.type_of_facilities,
        number_of_staff: formData.number_of_staff,
        has_emergency_training: formData.has_emergency_training,
        training_type: formData.training_type,
        have_emergency_plan: formData.have_emergency_plan,
        emergency_plans: formData.emergency_plans,
        message: formData.message
        // Note: Backend generates the HTML email template using the email field above
        // No need to send html, to, toName, or subject - backend handles all of that
      };

      // CRITICAL: Verify the payload email before sending
      if (applicationPayload.email !== finalEmail) {
        console.error("❌ CRITICAL ERROR: Payload email mismatch!");
        console.error("  Expected:", finalEmail);
        console.error("  Actual:", applicationPayload.email);
        setIsOpenNotification(true);
        setErrorMessage("Email validation error. Please refresh the page and try again.");
        setIsLoading(false);
        return;
      }

      console.log("📤 Sending application payload:");
      console.log("  Payload email field:", applicationPayload.email);
      console.log("  Email matches finalEmail:", applicationPayload.email === finalEmail);
      console.log("  Full payload (email field only):", { email: applicationPayload.email });

      // Add timeout to prevent hanging (30 seconds should be enough)
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/marketing/create-application`, 
        applicationPayload,
        { timeout: 30000 } // 30 second timeout
      );
      
      console.log("✅ Application submitted successfully:", response.data);

      setIsOpenNotification(true);
      setSuccessMessage("Your application submitted successfully!");
      setIsLoading(false);
      setFormData({
        applicant_name: "",
        center_school_name: "",
        position_title: "",
        phone: "",
        email: "",
        street_address: "",
        city: "",
        state: "",
        zipcode: "",
        type_of_facilities: [],
        number_of_staff: "",
        has_emergency_training: false,
        training_type: "",
        have_emergency_plan: false,
        emergency_plans: [],
        message: "",
      });
    } catch (err) {
      setIsOpenNotification(true);
      setErrorMessage(err?.response?.data?.error || err);
      setIsLoading(false);
    }
  }


  const handleCloseNotification = () => {
    setIsOpenNotification(false);
    setSuccessMessage("");
    setErrorMessage("");
  }

  return (
    <div>
      <div className="text-white relative">
        <Header />
        <div className="flex items-center justify-center mt-20 mb-15">
          <h3 className="lg:text-[26px] text-[18px] text-[#FF386A] mb-2 font-semibold w-fit text-center">AngelWatch Certification Grant Application</h3>
        </div>
        <div className="mt-5 gap-3 mx-auto text-gray-700 max-w-[900px] px-10">
          <div className="space-y-6">
            <div className="space-y-4">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1">Applicant Name (Owner/Director)</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.applicant_name} onChange={(e) => handleChangeFormData("applicant_name", e)} />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Center or School Name</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.center_school_name} onChange={(e) => handleChangeFormData("center_school_name", e)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1">Position/Title</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.position_title} onChange={(e) => handleChangeFormData("position_title", e)} />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Phone</label>
                  <input type="tel" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.phone} onChange={(e) => handleChangeFormData("phone", e)} />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Email</label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.email}
                    onChange={(e) => handleChangeFormData("email", e)}
                    onBlur={handleEmailBlur}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                    name="grant-application-email"
                    id="grant-application-email"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">

              <div>
                <label className="block text-gray-700 mb-1">Street Address</label>
                <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.street_address} onChange={(e) => handleChangeFormData("street_address", e)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1">City</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.city} onChange={(e) => handleChangeFormData("city", e)} />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">State</label>
                  <select
                    className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    value={formData.state}
                    onChange={(e) => handleChangeFormData("state", e)}
                  >
                    {US_STATE_ABBREVIATIONS.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">ZIP</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.zipcode} onChange={(e) => handleChangeFormData("zipcode", e)} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className="font-semibold text-gray-700">Type of Facility</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {TYPE_OF_FACILITY_OPTIONS.map((option, index) => (
                  <label className="inline-flex items-center" key={index}>
                    <input
                      type="checkbox"
                      value={option}
                      checked={formData.type_of_facilities.includes(option)}
                      onChange={(e) => handleChangeFormData("type_of_facilities", e)}
                      className="form-checkbox h-5 w-5 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-1">Number of Staff Members You Plan to Certify</label>
                <input type="number" min={1} className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.number_of_staff} onChange={(e) => handleChangeFormData("number_of_staff", e)} />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Has your staff received formal emergency protocol training in the past?</label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="training"
                      value={true}
                      checked={formData.has_emergency_training === true}
                      onChange={(e) => handleChangeFormData("has_emergency_training", e)}
                      className="form-radio h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="training"
                      value={false}
                      checked={formData.has_emergency_training === false}
                      onChange={(e) => handleChangeFormData("has_emergency_training", e)}
                      className="form-radio h-5 w-5 text-blue-600" />
                    <span className="ml-2 text-gray-700">No</span>
                  </label>
                </div>
                <div className="mt-2">
                  <label className="block text-gray-700 mb-1">If yes, when and what type:</label>
                  <input type="text" className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={formData.training_type} onChange={(e) => handleChangeFormData("training_type", e)} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-gray-700 mb-2">Do you currently have an Emergency Operations Plan (EOP)?</label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="eop"
                      value={true}
                      className="form-radio h-5 w-5 text-blue-600"
                      checked={formData.have_emergency_plan === true}
                      onChange={(e) => handleChangeFormData("have_emergency_plan", e)}
                    />
                    <span className="ml-2 text-gray-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="eop"
                      value={false}
                      checked={formData.have_emergency_plan === false}
                      onChange={(e) => handleChangeFormData("have_emergency_plan", e)}
                      className="form-radio h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">No</span>
                  </label>
                </div>
              </div>

              <div className="ml-6 space-y-2">
                <label className="block text-gray-700 mb-1">If yes, does it include:</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {EMERGENCY_OPERATIONS_PLAN.map((option, index) => (
                    <label className="inline-flex items-center" key={index}>
                      <input
                        type="checkbox"
                        value={option}
                        checked={formData.emergency_plans.includes(option)}
                        onChange={(e) => handleChangeFormData("emergency_plans", e)}
                        className="form-checkbox h-5 w-5 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 mb-1">Tell us why your school, center, or staff should be considered for a certification grant:</label>
              <p className="text-sm text-gray-500 mt-1">(You may include safety concerns, lack of access to effective training, past experiences, etc.)</p>
              <textarea className="w-full px-3 py-2 border border-[#333] rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" rows="4" value={formData.message} onChange={(e) => handleChangeFormData("message", e)}></textarea>
            </div>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          className="bg-[#F0AA00] h-[50px] sm:h-[55px] text-white text-base sm:text-lg font-semibold px-5 py-2 rounded-md hover:bg-yellow-500 transition flex justify-center items-center mb-10 w-[300px] mx-auto block mt-10"
          disabled={isLoading ? true : false}
        >
          {isLoading ? <FaSpinner className="animate-spin" /> : "Submit Application"}
        </button>
        <div className="mt-10 gap-3 mx-auto text-[10px] text-gray-700 max-w-[900px] px-10">
          <strong>Important Grant Terms & Disclosures</strong>
        </div>
        <div className="my-2 mb-10 gap-3 mx-auto text-[10px] text-gray-700 max-w-[900px] px-10">
          The following information is collected solely for grant qualification purposes and will never be shared with any third party. All AngelWatch grants—including those for the <strong>National Accreditation Program</strong> and the <strong>Emergency Certification Program</strong>—are offered on a <strong>first-come, first-served basis</strong>, and are limited by state-specific allocations. Applicants will be notified within <strong>five (5) business days</strong> if they qualify. If approved, you will have <strong>seven (7) days</strong> to complete enrollment. Failure to enroll within this window will result in your grant being reallocated to another center on the waitlist. The AngelWatch Foundation does not discriminate based on race, color, creed, gender, gender expression, age, national origin, disability, marital status, or sexual orientation. By enrolling in the <strong>AngelWatch National Accreditation Program</strong>, the listed owner, director, or authorized contact agrees to a <strong>minimum one-year commitment</strong> for any applicable <strong>parent pass-through cost</strong>. This requirement <strong>does not apply to the Emergency Certification Program</strong>. All training content, technologies, software systems, and equipment are the exclusive intellectual property of <strong>Angel Watch Corporation</strong>. Any <strong>non-compliance with program rules, misrepresentation, or misuse</strong> of grant funding will result in the <strong>revocation of all certifications or accreditations</strong>, and may include the <strong>immediate removal of access to digital platforms, training credentials, physical equipment, and public recognition</strong> of participation.
        </div>
        <Snackbar
          open={isOpenNotification}
          autoHideDuration={500000}
          onClose={handleCloseNotification}
          style={{
            position: "absolute",
            bottom: -65,
            right: 0,
            margin: "0 auto",
            maxWidth: 800,
          }}
        >
          <Alert onClose={handleCloseNotification} severity={successMessage ? "success" : "error"} sx={{ width: '100%', fontSize: "18px", backgroundColor: successMessage ? "#95ff95" : "#f2acac", }}>
            {successMessage || errorMessage}
          </Alert>
        </Snackbar>
      </div>
      <Footer />
    </div>
  );
}
